/**
 * TypeScript mirrors of `backend/app/schemas.py`.
 *
 * These are hand-written, so they are a promise, not a proof. Anything changed
 * in `schemas.py` must be changed here in the same commit — the compiler cannot
 * see the Python side.
 */
import type { TranslationKey } from '../i18n'

/** `app.services.metrics.Window`. These strings are the `window` query value. */
export const WINDOW_IDS = ['30m', '6h', '12h', '24h', '72h', '7d'] as const
export type WindowId = (typeof WINDOW_IDS)[number]
export const DEFAULT_WINDOW: WindowId = '24h'

export function isWindowId(value: unknown): value is WindowId {
  return typeof value === 'string' && (WINDOW_IDS as readonly string[]).includes(value)
}

/**
 * How often an open tab re-asks for a window, mirroring `WINDOW_TTL` in
 * `app/services/metrics.py`.
 *
 * Polling faster than the server's cache TTL only re-serves the same snapshot;
 * polling slower (or, as before, never) leaves a tab rendering numbers the
 * backend replaced an hour ago while the freshness badge beside them keeps
 * counting up. The scheduler warms every window every 60s, so a window's TTL is
 * also the longest a newer snapshot can sit there unnoticed.
 */
export const WINDOW_REFRESH_MS: Record<WindowId, number> = {
  '30m': 60_000,
  '6h': 300_000,
  '12h': 600_000,
  '24h': 900_000,
  '72h': 1_800_000,
  '7d': 3_600_000,
}

/**
 * The six window labels, as translation keys.
 *
 * A module is not a component, so it cannot hold a translated string: a string
 * baked in here would be frozen in whatever language was active when the module
 * was first evaluated and would not change when the user switches. The KEY goes
 * in the data and `t()` is called at render time — see `src/i18n/en.ts`.
 *
 * The WindowId keys ('30m', '7d') are the `window` query value and are never
 * translated. There is deliberately no English-text twin of this map: the one
 * that used to sit here was still being read by four call sites that cast it
 * `as TranslationKey`, and since `translate()` echoes an unrecognised key back
 * verbatim, those rendered 'Last 24 hours' inside Chinese sentences with a
 * green build. A table of keys is the only table.
 */
export const WINDOW_LABEL_KEYS: Record<WindowId, TranslationKey> = {
  '30m': 'api.window.30m',
  '6h': 'api.window.6h',
  '12h': 'api.window.12h',
  '24h': 'api.window.24h',
  '72h': 'api.window.72h',
  '7d': 'api.window.7d',
}

/** `app.services.health.HealthState`. `unknown` is the majority state: most of
 *  the 1,062 registry endpoints see no traffic in any given window. */
export const HEALTH_STATES = ['healthy', 'degraded', 'down', 'unknown'] as const
export type HealthState = (typeof HEALTH_STATES)[number]

/**
 * `schemas.Meta` — the freshness envelope on every data response.
 *
 * Cloudflare's analytics are adaptive-sampled and lag real time, so a bare
 * number on screen would be a lie by omission. Every view that renders counts
 * must also render this.
 */
export interface Meta {
  /** ISO-8601 UTC end of the data window — NOT "now"; the query is backdated. */
  as_of: string
  age_seconds: number
  /** True when Cloudflare sampled: the counts are estimates scaled up. */
  sampled: boolean
  avg_sample_interval: number | null
  /** Served from a stale cache because the upstream query failed. */
  degraded: boolean
  cf_queries: number
  window: string | null
}

/**
 * `schemas.EndpointSummary`.
 *
 * `total === null` means "no data for this window" and is deliberately distinct
 * from `0`. Rendering a null as 0 would claim a silent endpoint is perfectly
 * healthy, which is a different and false statement.
 */
export interface EndpointSummary {
  endpoint_id: string
  path: string
  method: string
  family: string
  total: number | null
  /** Keyed by HTTP status code. JSON object keys are strings at runtime. */
  by_status: Record<string, number>
  ratios: Record<string, number>
  health: HealthState
  error_rate: number | null
  /** Only meaningful for windows shorter than the 24h baseline; always false
   *  on 24h/72h/7d because those windows contain the baseline (see
   *  `app/routes/metrics.py`). The UI must suppress the badge there rather
   *  than read `false` as "no cliff". */
  traffic_cliff: boolean
}

export interface EndpointListResponse {
  meta: Meta
  /** Rows matching the filters BEFORE limit/offset — for the pager. */
  total_count: number
  items: EndpointSummary[]
}

export interface FamilySummary {
  family: string
  endpoint_count: number
  with_traffic: number
  total_requests: number
  health_counts: Record<HealthState, number>
}

export interface OverviewResponse {
  meta: Meta
  total_requests: number
  by_status: Record<string, number>
  ratios: Record<string, number>
  health_counts: Record<HealthState, number>
  families: FamilySummary[]
}

export interface EndpointDetailResponse {
  meta: Meta
  endpoint: EndpointSummary
  /** Keyed by window id, but only windows already in cache are present. */
  windows: Partial<Record<WindowId, EndpointSummary>>
}

export interface FamilyListResponse {
  meta: Meta
  families: FamilySummary[]
}

/** `schemas.MeResponse` — `GET /auth/me`. */
export interface MeResponse {
  id: number
  email: string
  role: 'superadmin' | 'member' | string
  totp_enabled: boolean
  /** `users.totp_required`: this account may not leave 2FA off. Set on every
   *  invited account, and still set after a superadmin resets their 2FA -- so a
   *  signed-in user can be looking at their profile with a forced enrolment
   *  gate waiting at their next login. The profile page says so because of this
   *  field; the server is what enforces it. */
  totp_required: boolean
  must_change_password: boolean
}

/**
 * `POST /auth/login`. Three mutually exclusive answers, and only the last one
 * is a session:
 *
 *  * `mfa_required` — the password was right and a TOTP code is still owed;
 *    the caller gets an `mfa_token` for `/auth/2fa/verify` and NO cookies.
 *  * `enrollment_required` — the account owes a second factor it has never set
 *    up (`users.totp_required`). Again no cookies: the `enrollment_token` opens
 *    `/auth/2fa/enroll` and `/auth/2fa/enroll/confirm` and nothing else in the
 *    service, so abandoning enrollment means meeting it again at the next login
 *    rather than being let in.
 *  * neither — the cookies are set and the user is in.
 */
export interface LoginResponse {
  mfa_required: boolean
  mfa_token?: string
  enrollment_required?: boolean
  /** Scoped `enrollment` token, 10 minutes. Never put in a URL. */
  enrollment_token?: string
  must_change_password: boolean
}

/** `POST /auth/2fa/verify`. */
export interface TwoFactorResponse {
  ok: boolean
  must_change_password: boolean
}

/**
 * `schemas.MIN_PASSWORD_LENGTH` — the ONE floor every password path enforces.
 *
 * Invite acceptance and self-service rotation share it on the server, and they
 * have to: forced rotation routes every account through the rotation endpoint
 * within minutes of signup, so two numbers would mean the weaker one is the
 * real policy. Checking it client side only spares the user a round trip; the
 * server rejects a short password with a 422 regardless.
 */
export const MIN_PASSWORD_LENGTH = 12

/** `POST /auth/password`. The response also re-sets the session cookies, so the
 *  caller stays signed in on this browser while every other session dies. */
export interface PasswordChangeResponse {
  ok: boolean
  /** Always `false` on success: the change is what clears the forced rotation. */
  must_change_password: boolean
}

/** The body of both enrollment calls when the caller holds no session — an
 *  invitee, or a login that answered `enrollment_required`. Omitted entirely on
 *  the profile page, where the session cookie is the credential. */
export interface EnrollmentTokenBody {
  enrollment_token?: string
}

/** `POST /auth/2fa/enroll` — a pending secret, stored with `totp_enabled` still
 *  false. Nothing is enabled until the confirm step accepts a code. */
export interface EnrollStartResponse {
  secret: string
  otpauth_uri: string
  /** A `data:image/png;base64,...` URI rendered by segno server-side; there is
   *  no QR library in the bundle and no third-party image request. */
  qr_data_uri: string
}

/** `POST /auth/2fa/enroll/confirm`. The codes are stored Argon2-hashed, so this
 *  response is the only time they exist in readable form, anywhere, ever. */
export interface EnrollConfirmResponse {
  ok: boolean
  backup_codes: string[]
  /** True when the scoped enrollment token graduated into real session cookies
   *  on this very response — the requirement is satisfied, so the user goes
   *  into the app rather than back to a login form. False on the cookie path,
   *  which already had a session. */
  session_started?: boolean
  must_change_password?: boolean
}

/** Query parameters of `GET /api/v1/endpoints`. */
export interface EndpointQuery {
  window?: WindowId
  family?: string
  health?: HealthState
  q?: string
  limit?: number
  offset?: number
  [key: string]: string | number | boolean | undefined
}

/* ------------------------------------------------------------------ */
/* Superadmin team management (`app/routes/admin.py`).                 */
/* ------------------------------------------------------------------ */

/** The two roles in `models.Role`. Widened with `string` nowhere: an unknown
 *  role would be a backend change this file has to be updated for. */
export const ROLES = ['member', 'superadmin'] as const
export type RoleId = (typeof ROLES)[number]

/**
 * The two roles, as translation keys — the role equivalent of
 * `WINDOW_LABEL_KEYS`, and for the same reason.
 *
 * Shared from here rather than copied per screen: `AccountSummary` (profile),
 * `InviteMemberForm` (the role picker) and `TeamMemberRow` (the team table)
 * all render the same value, and three private copies is how the team table
 * ended up printing a raw `superadmin` under a 角色 header while the profile
 * page said 超级管理员 for the same account.
 *
 * An id this build has never heard of is rendered verbatim by the call sites
 * (`ROLE_LABEL_KEYS[role]` is then `undefined`), because a role we cannot name
 * is exactly what a reader needs to see.
 */
export const ROLE_LABEL_KEYS: Record<RoleId, TranslationKey> = {
  member: 'domain.role.member',
  superadmin: 'domain.role.superadmin',
}

/** `admin.UserSummary` — `GET /api/v1/admin/users`. Deliberately narrow on the
 *  server side too: no password hash, no encrypted TOTP secret. */
export interface AdminUserSummary {
  id: number
  email: string
  role: string
  is_active: boolean
  totp_enabled: boolean
  /** Without this, "2FA: Off" cannot tell a grandfathered account that is
   *  allowed to stay that way from an invited one that is sitting in the
   *  enrolment gate, locked out of the product until it enrols. */
  totp_required: boolean
  must_change_password: boolean
  created_at: string
}

/** `admin.InviteResponse` — `POST /api/v1/admin/invites`, 201.
 *
 *  `invite_token` is the plaintext of a token the server stores only as a
 *  SHA-256 hash. It is in this response and nowhere else, ever. */
export interface InviteResponse {
  invite_token: string
  email: string
  role: string
  expires_at: string
}

/** `schemas.AdminPasswordResetResponse`. `temporary_password` is likewise
 *  returned exactly once and never stored in readable form. */
export interface AdminPasswordResetResponse {
  ok: boolean
  user_id: number
  email: string
  temporary_password: string
}

/** `schemas.Disable2FAResponse` — 2FA recovery for a lost authenticator. */
export interface Disable2FAResponse {
  ok: boolean
  user_id: number
  email: string
  totp_enabled: boolean
}

/** `schemas.UserDeleteResponse` -- `DELETE /api/v1/admin/users/{id}`.
 *
 *  The counts are what the admin needs to understand what they just did.
 *  `invites_revoked` is how many live invites this account was still holding
 *  open (each one mints a whole new account, so they die with their creator),
 *  and `audit_entries_anonymised` is the half that is deliberately NOT a
 *  deletion: those rows survive with `user_id` NULL and the email preserved in
 *  their detail, so the account's history is still on file. */
export interface UserDeleteResponse {
  ok: boolean
  user_id: number
  email: string
  invites_revoked: number
  audit_entries_anonymised: number
}

/** `POST /api/v1/admin/users/{id}/deactivate`. */
export interface DeactivateResponse {
  ok: boolean
  id: number
  is_active: boolean
}

/** `POST /auth/invite/accept`, 201 — the only way an account is ever created.
 *
 *  Unauthenticated: the invite token IS the credential, which is why it travels
 *  in a URL fragment rather than a query string (see `inviteUrl`). */
export interface InviteAcceptResponse {
  ok: boolean
  user_id: number
  email: string
  /** Canonical since the enrollment gate; `totp_enrollment_required` is the
   *  retained alias for the same fact. Always true today — every invited
   *  account, member and superadmin alike, owes a second factor. */
  enrollment_required?: boolean
  totp_enrollment_required?: boolean
  /** The scoped `enrollment` token. NO session cookies are set here: this is
   *  the only credential the new account holds until a TOTP code verifies. */
  enrollment_token?: string
}
